import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GarminAiAttribution, RideAttribution } from '../components/GarminAttribution.tsx';
import { PageShell } from '../components/PageShell.tsx';
import { skeletonKeys } from '../lib/skeletonKeys.ts';
import {
  useActivateBlock,
  useAnalyzeDay,
  useBlock,
  useBlockStats,
  useDeleteBlock,
  useEndBlock,
  useGenerateBlockSummary,
} from '../hooks/useBlocks.ts';
import type {
  BlockStats,
  TrainingBlockDay,
  TrainingBlockDetail,
  TrainingBlockExecution,
} from '../types/api.ts';
import {
  blockStatusClass,
  dayState,
  dayStateClass,
  dayStateLabel,
  dateForDayIndex,
  formatBlockDay,
  formatBlockStatus,
  formatDateRange,
  nextMondayIso,
  workoutChipClass,
  workoutLabel,
} from '../lib/blockFormat.ts';
import {
  formatDistanceKm,
  formatDuration,
} from '../lib/rideFormat.ts';

export function BlockDetail() {
  const { id } = useParams();
  const blockId = id ? Number(id) : null;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: block, isLoading, isError } = useBlock(blockId);
  const { data: stats } = useBlockStats(blockId);
  const activateMut = useActivateBlock();
  const endMut = useEndBlock();
  const deleteMut = useDeleteBlock();
  const summaryMut = useGenerateBlockSummary();
  const [summaryError, setSummaryError] = useState('');

  const handleGenerateSummary = async () => {
    if (!block) return;
    setSummaryError('');
    try {
      await summaryMut.mutateAsync(block.id);
    } catch (err) {
      setSummaryError(extractError(err, t('blocks.detail.summaryFailed')));
    }
  };

  const [selectedDayId, setSelectedDayId] = useState<number | null>(null);
  const [actionError, setActionError] = useState('');
  // Default the Activate-flow start date to the upcoming Monday so
  // weekly-structured blocks (the typical Mon-Sun shape) line up with
  // their first day. If today is Monday, default to today; otherwise
  // the next Monday. User can override via the date input before
  // clicking Activate.
  const [activateStartDate, setActivateStartDate] = useState<string>(
    () => nextMondayIso(new Date()),
  );

  const selectedDay = useMemo(
    () => block?.days.find((d) => d.id === selectedDayId) ?? null,
    [block, selectedDayId],
  );

  const handleActivate = async () => {
    if (!block) return;
    setActionError('');
    try {
      await activateMut.mutateAsync({
        id: block.id,
        body: activateStartDate ? { start_date: activateStartDate } : undefined,
      });
    } catch (err) {
      setActionError(extractError(err, t('blocks.detail.activateFailed')));
    }
  };

  const handleEnd = async () => {
    if (!block) return;
    if (!globalThis.confirm(t('blocks.detail.confirmEnd'))) return;
    setActionError('');
    try {
      await endMut.mutateAsync(block.id);
    } catch (err) {
      setActionError(extractError(err, t('blocks.detail.endFailed')));
    }
  };

  const handleDelete = async () => {
    if (!block) return;
    if (!globalThis.confirm(t('blocks.detail.confirmDelete'))) return;
    setActionError('');
    try {
      await deleteMut.mutateAsync(block.id);
      navigate('/blocks');
    } catch (err) {
      setActionError(extractError(err, t('blocks.detail.deleteFailed')));
    }
  };

  return (
    <PageShell title={block?.name ?? ' '}>
      <div className="flex flex-col gap-5">
        <button
          onClick={() => navigate('/blocks')}
          className="self-start text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1"
        >
          <span aria-hidden="true">{'←'}</span>
          {t('blocks.detail.back')}
        </button>

        {isLoading && <DetailSkeleton />}

        {isError && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 text-sm">
            {t('blocks.detail.errorLoading')}
          </div>
        )}

        {actionError && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 text-sm">
            {actionError}
          </div>
        )}

        {block && (
          <>
            <BlockHeader
              block={block}
              activatePending={activateMut.isPending}
              endPending={endMut.isPending}
              deletePending={deleteMut.isPending}
              startDate={activateStartDate}
              onStartDateChange={setActivateStartDate}
              onActivate={handleActivate}
              onEnd={handleEnd}
              onDelete={handleDelete}
            />

            {stats && block.status !== 'draft' && (
              <StatsCard
                stats={stats}
                summary={summaryMut.data?.summary ?? block.coach_summary}
                summaryAt={
                  summaryMut.data?.generated_at ?? block.coach_summary_at
                }
                summarySources={summaryMut.data?.ai_sources ?? block.ai_sources}
                summaryError={summaryError}
                summaryPending={summaryMut.isPending}
                onGenerateSummary={handleGenerateSummary}
              />
            )}

            <div className="flex flex-col gap-2">
              {block.days.map((day) => (
                <DayRow
                  key={day.id}
                  day={day}
                  block={block}
                  isSelected={day.id === selectedDayId}
                  onSelect={() =>
                    setSelectedDayId((cur) => (cur === day.id ? null : day.id))
                  }
                />
              ))}
            </div>

            {selectedDay && (
              <DayPanel
                day={selectedDay}
                block={block}
                onClose={() => setSelectedDayId(null)}
              />
            )}
          </>
        )}
      </div>
    </PageShell>
  );
}

function BlockHeader({
  block,
  activatePending,
  endPending,
  deletePending,
  startDate,
  onStartDateChange,
  onActivate,
  onEnd,
  onDelete,
}: Readonly<{
  block: TrainingBlockDetail;
  activatePending: boolean;
  endPending: boolean;
  deletePending: boolean;
  startDate: string;
  onStartDateChange: (iso: string) => void;
  onActivate: () => void;
  onEnd: () => void;
  onDelete: () => void;
}>) {
  const { t } = useTranslation();
  const dayUnit = block.duration_days === 1 ? 'day' : 'days';
  return (
    <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-(--color-text-secondary)">
            {block.start_date && block.end_date
              ? formatDateRange(block.start_date, block.end_date)
              : `${block.duration_days} ${dayUnit}`}
          </p>
          {block.description && (
            <p className="text-sm text-(--color-text-primary) mt-1">
              {block.description}
            </p>
          )}
        </div>
        <span
          className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${blockStatusClass(block.status)}`}
        >
          {formatBlockStatus(block.status)}
        </span>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {block.status === 'draft' && (
          <>
            <label className="flex items-center gap-2 text-xs text-(--color-text-secondary)">
              <span>{t('blocks.detail.startDateLabel')}</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
                disabled={activatePending}
                className="bg-(--color-bg-tertiary) border border-(--color-border) rounded-md px-2 py-1 text-(--color-text-primary) text-sm focus:outline-none focus:border-(--color-accent) disabled:opacity-50"
              />
            </label>
            <button
              onClick={onActivate}
              disabled={activatePending || !startDate}
              className="px-4 py-2 rounded-lg bg-green-500/15 text-green-400 hover:bg-green-500/25 transition-colors text-sm font-medium border border-green-500/40 disabled:opacity-50"
            >
              {activatePending
                ? t('blocks.detail.activating')
                : t('blocks.detail.activate')}
            </button>
          </>
        )}
        {block.status === 'active' && (
          <button
            onClick={onEnd}
            disabled={endPending}
            className="px-4 py-2 rounded-lg bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors text-sm font-medium border border-red-500/40 disabled:opacity-50"
          >
            {endPending ? t('blocks.detail.ending') : t('blocks.detail.endBlock')}
          </button>
        )}
        <button
          onClick={onDelete}
          disabled={deletePending}
          className="ml-auto px-3 py-2 rounded-lg text-(--color-text-secondary) hover:text-red-400 hover:bg-red-500/10 transition-colors text-xs font-medium border border-(--color-border) disabled:opacity-50"
        >
          {deletePending ? t('blocks.detail.deleting') : t('blocks.detail.delete')}
        </button>
      </div>
    </div>
  );
}

function StatsCard({
  stats,
  summary,
  summaryAt,
  summaryError,
  summaryPending,
  onGenerateSummary,
  summarySources,
}: Readonly<{
  stats: BlockStats;
  summary: string | null;
  summaryAt: string | null;
  summarySources?: string[];
  summaryError: string;
  summaryPending: boolean;
  onGenerateSummary: () => void;
}>) {
  const { t } = useTranslation();
  const totalHours = stats.totals.total_hours;
  const totalKm = stats.totals.total_distance_km;
  const totalTss = Math.round(stats.totals.total_tss);
  const adherence =
    stats.adherence_pct === null ? '—' : `${stats.adherence_pct}%`;
  const ridesLine = `${stats.totals.rides_executed} / ${stats.totals.rides_planned}`;
  const hardLine = `${stats.hard_days.completed} / ${stats.hard_days.planned}`;
  const restLine = `${stats.totals.rest_days_respected} / ${stats.totals.rest_days_planned}`;
  const summaryLabel = summary
    ? `🤖 ${t('blocks.detail.regenerateSummary')}`
    : `🤖 ${t('blocks.detail.generateSummary')}`;

  return (
    <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
          {t('blocks.detail.statsTitle')}
        </h3>
        <button
          onClick={onGenerateSummary}
          disabled={summaryPending}
          className="px-3 py-1 rounded-lg bg-(--color-accent)/15 text-(--color-accent) hover:bg-(--color-accent)/25 transition-colors text-xs font-medium border border-(--color-accent)/30 disabled:opacity-50"
        >
          {summaryPending ? t('blocks.detail.generatingSummary') : summaryLabel}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <Stat label={t('blocks.detail.statRides')} value={ridesLine} />
        <Stat label={t('blocks.detail.statAdherence')} value={adherence} />
        <Stat
          label={t('blocks.detail.statTotalHours')}
          value={`${totalHours.toFixed(1)} h`}
        />
        <Stat
          label={t('blocks.detail.statTotalDistance')}
          value={`${totalKm.toFixed(1)} km`}
        />
        <Stat
          label={t('blocks.detail.statTotalTss')}
          value={`${totalTss}`}
        />
        <Stat label={t('blocks.detail.statHardDays')} value={hardLine} />
        <Stat label={t('blocks.detail.statRestDays')} value={restLine} />
        <Stat
          label={t('blocks.detail.statMissed')}
          value={`${stats.totals.rides_missed}`}
        />
      </div>

      {(stats.highlights.longest_ride_duration_sec ||
        stats.highlights.hardest_ride_intensity_factor !== null ||
        stats.highlights.highest_tss_value !== null) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-(--color-text-secondary) pt-2 border-t border-(--color-border)">
          {stats.highlights.longest_ride_duration_sec && (
            <span>
              {t('blocks.detail.highlightLongest')}:{' '}
              <span className="text-(--color-text-primary)">
                {Math.round(
                  stats.highlights.longest_ride_duration_sec / 60,
                )}{' '}
                min
              </span>
            </span>
          )}
          {stats.highlights.hardest_ride_intensity_factor !== null && (
            <span>
              {t('blocks.detail.highlightHardest')}:{' '}
              <span className="text-(--color-text-primary)">
                IF {stats.highlights.hardest_ride_intensity_factor.toFixed(2)}
              </span>
            </span>
          )}
          {stats.highlights.highest_tss_value !== null && (
            <span>
              {t('blocks.detail.highlightTopTss')}:{' '}
              <span className="text-(--color-text-primary)">
                {Math.round(stats.highlights.highest_tss_value)} TSS
              </span>
            </span>
          )}
        </div>
      )}

      {summaryError && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 text-xs">
          {summaryError}
        </div>
      )}

      {summary && (
        <div className="border-t border-(--color-border) pt-3 flex flex-col gap-1">
          <h4 className="text-xs text-(--color-text-secondary) uppercase tracking-wide">
            {t('blocks.detail.summaryTitle')}
          </h4>
          <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-3 text-sm text-(--color-text-primary) whitespace-pre-wrap leading-relaxed">
            {summary}
          </div>
          <GarminAiAttribution sources={summarySources} />
          {summaryAt && (
            <p className="text-[11px] text-(--color-text-secondary)">
              {t('blocks.detail.generatedAt', {
                when: new Date(summaryAt).toLocaleString(),
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DayRow({
  day,
  block,
  isSelected,
  onSelect,
}: Readonly<{
  day: TrainingBlockDay;
  block: TrainingBlockDetail;
  isSelected: boolean;
  onSelect: () => void;
}>) {
  const date = dateForDayIndex(block, day.day_index);
  const hasExecution = day.executions.length > 0;
  const state = dayState(day, date, hasExecution);
  const { t } = useTranslation();

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left rounded-lg p-3 border transition-colors flex flex-col gap-2 ${
        isSelected
          ? 'bg-(--color-bg-tertiary) border-(--color-accent)'
          : 'bg-(--color-bg-secondary) border-(--color-border) hover:border-(--color-accent)/40'
      }`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-xs text-(--color-text-secondary) w-28 shrink-0">
            <div className="font-medium">
              {t('blocks.detail.day')} {day.day_index + 1}
            </div>
            <div>{formatBlockDay(date)}</div>
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-(--color-text-primary) truncate">
              {day.title ?? workoutLabel(day.workout_type)}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border ${workoutChipClass(day.workout_type)}`}
              >
                {workoutLabel(day.workout_type)}
              </span>
              {day.target_duration_min !== null && (
                <span className="text-xs text-(--color-text-secondary)">
                  {day.target_duration_min}min
                </span>
              )}
              {day.target_power_low_pct !== null &&
                day.target_power_high_pct !== null && (
                  <span className="text-xs text-(--color-text-secondary)">
                    {day.target_power_low_pct}–{day.target_power_high_pct}%FTP
                  </span>
                )}
            </div>
          </div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${dayStateClass(state)}`}
        >
          {dayStateLabel(state)}
        </span>
      </div>

      {hasExecution && (
        <ExecutionStrip executions={day.executions} />
      )}
    </button>
  );
}

function ExecutionStrip({
  executions,
}: Readonly<{ executions: TrainingBlockExecution[] }>) {
  return (
    <div className="flex flex-col gap-1 pl-28 text-xs text-(--color-text-secondary)">
      {executions.map((e) => (
        <div key={e.id} className="flex items-center gap-2 flex-wrap">
          <span className="text-green-400">{'✓'}</span>
          <span>{formatDuration(e.duration_sec)}</span>
          {e.distance_m !== null && (
            <span>· {formatDistanceKm(e.distance_m)}</span>
          )}
          {e.normalized_power !== null && (
            <span>· NP {e.normalized_power}W</span>
          )}
          {e.avg_hr !== null && <span>· HR {e.avg_hr}bpm</span>}
          {e.training_stress_score !== null && (
            <span>· TSS {Math.round(e.training_stress_score)}</span>
          )}
          <RideAttribution text={e.attribution} />
        </div>
      ))}
    </div>
  );
}

function DayPanel({
  day,
  block,
  onClose,
}: Readonly<{
  day: TrainingBlockDay;
  block: TrainingBlockDetail;
  onClose: () => void;
}>) {
  const { t } = useTranslation();
  const date = dateForDayIndex(block, day.day_index);
  const analyzeMut = useAnalyzeDay();
  const [analyzeError, setAnalyzeError] = useState('');

  const handleAnalyze = async () => {
    setAnalyzeError('');
    try {
      await analyzeMut.mutateAsync({ blockId: block.id, dayId: day.id });
    } catch (err) {
      setAnalyzeError(extractError(err, t('blocks.detail.deepDiveFailed')));
    }
  };
  const analyzeLabel = day.coach_analysis
    ? `🤖 ${t('blocks.detail.regenerate')}`
    : `🤖 ${t('blocks.detail.deepDive')}`;
  return (
    <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-accent)/40 p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-(--color-text-primary)">
            {t('blocks.detail.day')} {day.day_index + 1}
            {date && ` · ${formatBlockDay(date)}`}
          </h3>
          {day.title && (
            <p className="text-sm text-(--color-text-primary) mt-1">
              {day.title}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-lg px-2"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <Stat label={t('blocks.detail.workoutType')} value={workoutLabel(day.workout_type)} />
        <Stat
          label={t('blocks.detail.targetDuration')}
          value={day.target_duration_min == null ? '—' : `${day.target_duration_min} min`}
        />
        <Stat
          label={t('blocks.detail.targetPower')}
          value={
            day.target_power_low_pct === null || day.target_power_high_pct === null
              ? '—'
              : `${day.target_power_low_pct}–${day.target_power_high_pct}%FTP`
          }
        />
        <Stat
          label={t('blocks.detail.targetTss')}
          value={day.target_tss == null ? '—' : `${Math.round(day.target_tss)}`}
        />
      </div>

      {day.description && (
        <div>
          <h4 className="text-xs text-(--color-text-secondary) uppercase tracking-wide mb-1">
            {t('blocks.detail.description')}
          </h4>
          <p className="text-sm text-(--color-text-primary) whitespace-pre-wrap">
            {day.description}
          </p>
        </div>
      )}

      {day.intervals && day.intervals.length > 0 && (
        <div>
          <h4 className="text-xs text-(--color-text-secondary) uppercase tracking-wide mb-1">
            {t('blocks.detail.intervals')}
          </h4>
          <ul className="text-sm text-(--color-text-primary) space-y-1">
            {day.intervals.map((iv) => (
              <li key={iv.index}>
                <span className="text-(--color-text-secondary)">{iv.kind}:</span>{' '}
                {iv.duration_min}min
                {iv.target_power_low_pct !== null &&
                  iv.target_power_high_pct !== null && (
                    <span>
                      {' '}
                      @ {iv.target_power_low_pct}–{iv.target_power_high_pct}%FTP
                    </span>
                  )}
                {iv.repetitions > 1 && <span> × {iv.repetitions}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {day.executions.length > 0 && (
        <div>
          <h4 className="text-xs text-(--color-text-secondary) uppercase tracking-wide mb-1">
            {t('blocks.detail.executions')}
          </h4>
          <ul className="text-sm text-(--color-text-primary) space-y-1">
            {day.executions.map((e) => (
              <li key={e.id} className="flex items-center gap-2 flex-wrap">
                <span className="text-green-400">{'✓'}</span>
                <span>{formatDuration(e.duration_sec)}</span>
                {e.distance_m !== null && <span>· {formatDistanceKm(e.distance_m)}</span>}
                {e.normalized_power !== null && <span>· NP {e.normalized_power}W</span>}
                {e.avg_hr !== null && <span>· HR {e.avg_hr}bpm</span>}
                {e.training_stress_score !== null && (
                  <span>· TSS {Math.round(e.training_stress_score)}</span>
                )}
                <RideAttribution text={e.attribution} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {day.notes && (
        <p className="text-xs text-(--color-text-secondary) whitespace-pre-wrap border-t border-(--color-border) pt-2">
          {day.notes}
        </p>
      )}

      <div className="border-t border-(--color-border) pt-3 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h4 className="text-xs text-(--color-text-secondary) uppercase tracking-wide">
            {t('blocks.detail.deepDiveTitle')}
          </h4>
          <button
            onClick={handleAnalyze}
            disabled={analyzeMut.isPending}
            className="px-3 py-1 rounded-lg bg-(--color-accent)/15 text-(--color-accent) hover:bg-(--color-accent)/25 transition-colors text-xs font-medium border border-(--color-accent)/30 disabled:opacity-50"
          >
            {analyzeMut.isPending ? t('blocks.detail.analyzing') : analyzeLabel}
          </button>
        </div>
        {analyzeError && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 text-xs">
            {analyzeError}
          </div>
        )}
        {(() => {
          // Prefer the just-returned mutation result so the analysis
          // appears immediately, before the block-detail query refetches.
          const analysisText =
            analyzeMut.data?.analysis ?? day.coach_analysis ?? null;
          const generatedAt =
            analyzeMut.data?.generated_at ?? day.coach_analysis_at ?? null;
          if (!analysisText) return null;
          return (
            <>
              <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-3 text-sm text-(--color-text-primary) whitespace-pre-wrap leading-relaxed">
                {analysisText}
              </div>
              <GarminAiAttribution sources={analyzeMut.data?.ai_sources ?? block.ai_sources} />
              {generatedAt && (
                <p className="text-[11px] text-(--color-text-secondary)">
                  {t('blocks.detail.generatedAt', {
                    when: new Date(generatedAt).toLocaleString(),
                  })}
                </p>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}

function Stat({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) p-2">
      <div className="text-[10px] text-(--color-text-secondary) uppercase tracking-wide">
        {label}
      </div>
      <div className="text-(--color-text-primary) font-medium">{value}</div>
    </div>
  );
}

const DETAIL_SKELETON_KEYS = skeletonKeys(6);

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      <div className="h-20 rounded-xl bg-(--color-bg-secondary) border border-(--color-border)" />
      {DETAIL_SKELETON_KEYS.map((key) => (
        <div
          key={key}
          className="h-16 rounded-lg bg-(--color-bg-secondary) border border-(--color-border)"
        />
      ))}
    </div>
  );
}

function extractError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: { error?: string } } }).response;
    if (resp?.data?.error) return resp.data.error;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

